import type Cookies from 'js-cookie';
import type { lightTheme as light, darkTheme as dark } from '@clds/themes';
import type { Account, Capability, DeprecatedData, Permission, Plan, ProductEnvironment, User } from '@cld/unicorn-api';
import type { AbTestingClient } from '../api/createAbTestingClient.js';

export type Primitive = string | number | boolean;

export type ProductName =
  | 'programmableMedia'
  | 'digitalAssetManagement'
  | 'accountSettings'
  | 'mediaOptimizer'
  | 'mediaFlows'
  | 'dimensions'
  | 'finalTouch'
  | 'video';

type ApiResult<Data> = Promise<{ kind: 'success'; data: Data } | { kind: 'error'; errorMessage: string }>;

export interface CookieApi {
  get: (typeof Cookies)['get'];
  set: (typeof Cookies)['set'];
}

export interface LocationApi {
  getHref(): string;
  redirect(this: void, newHref: string, option?: { replace?: boolean }): void;
}

export type GetAuthenticationToken = () => Promise<{ kind: 'success'; token: string } | { kind: 'error' }>;

export type SystemTheme = 'light' | 'dark' | 'auto';
export type StyledComponentsThemeObject = typeof light | typeof dark;

export type CloudinaryFetchClient = typeof window.fetch;

export type AbTestingClientState = { status: 'loading' } | { status: 'ready'; client: AbTestingClient };

export interface ConsoleApiClient {
  fetchAccount(): ApiResult<Account>;

  fetchPlan(): ApiResult<Plan>;

  fetchUser(): ApiResult<Omit<User, 'impersonationActive'> & { accountId: Account['id'] }>;
  fetchAllProductEnvironments(): ApiResult<Array<ProductEnvironment & { accountId: Account['id'] }>>;

  fetchProductEnvironment(externalId: string): ApiResult<ProductEnvironment & { accountId: Account['id'] }>;
  /** @deprecated Remove once calling it is not neccessary. */

  fetchLoginFromSession(): ApiResult<
    {
      capabilities: Capability[];
      productEnvironment: ProductEnvironment & { accountId: Account['id'] };
      permissions: Permission[];
      user: Omit<User, 'impersonationActive'> & { accountId: Account['id'] };
    } & DeprecatedData
  >;
  postGdprLog: (kind: string, subkind: string, metadata: string, details: string, justification: string) => ApiResult<void>;
}

export type StorageResult<ValueType> =
  | (ValueType extends void ? { status: 'success' } : { status: 'success'; value: ValueType })
  | { status: 'error'; type: 'missingValue' | 'malformedValue' };

export interface StorageClient {
  createController<ValueType extends Primitive | Record<string, Primitive> | Primitive[]>(
    itemName: string
  ): {
    get(): StorageResult<ValueType>;
    set(value: ValueType): StorageResult<void>;
    remove(): StorageResult<void>;
  };
}

export const consoleLoggerCategories = ['general', 'state', 'api', 'abTesting', 'analytics', 'storage', 'logger'] as const;
export type ConsoleLoggerCategory = (typeof consoleLoggerCategories)[number];
type ConsoleLoggerMethod = (category: ConsoleLoggerCategory, message: string, data?: unknown) => void;
export interface ConsoleLogger {
  debug: ConsoleLoggerMethod;
  error: ConsoleLoggerMethod;
  warning: ConsoleLoggerMethod;
  enabledCategories: ConsoleLoggerCategory[];
  toggleCategory: (category: ConsoleLoggerCategory) => void;
}
